import * as React from 'react';
import { useEffect, useState } from 'react';
import { Footer } from './Footer';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Header } from './Header';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { usePageTitle } from '../../Hooks/usePageTitle';
import { useTrans } from '../../Store/System/LocalizationStore';
import { CustomerGroupApi } from '../../Model/CustomerGroup/CustomerGroup';
import { CustomerGroupSelectionModal } from '../../Components/CustomerGroupSelectionModal';
import { usePlausibleTracking } from '../../Hooks/usePlausibleTracking';
import { PlausibleProvider } from '../../Contexts/PlausibleContext';
import { ServiceLifecycleCard } from '../../Components/ServiceLifecycleBoundary';

const Content = styled.main`
  flex: 1;
  min-height: calc(100vh - 64px);
  background-color: ${Theme.Colors.background};
`;

const routeTitles: Record<string, string> = {
  '/': 'Home',
  '/home': 'Home',
  '/move': 'Movement',
  '/move/schedule': 'Schedule',
  '/move/library': 'On Demand',
  '/move/fitness-programs': 'Fitness Programs',
  '/mental-health': 'Mental Wellness',
  '/mental-health/library': 'Mental Health Library',
  '/mental-health/mindfulness-programs': 'Mindfulness Programs',
  '/nutrition': 'Nutrition',
  '/nutrition/library': 'Nutrition Library',
  '/academy': 'Academy',
  '/academy/schedule': 'Schedule',
  '/academy/library': 'Academy Library',
  '/motivation': 'Motivation',
  '/motivation/history': 'Points History',
  '/motivation/community': 'Community',
  '/motivation/challenges': 'Challenges',
  '/schedule': 'Calendar',
  '/activities': 'Activities',
  '/account': 'Account',
  '/feedback': 'Feedback',
  '/contact': 'Contact',
  '/privacy-policy': 'Privacy Policy',
  '/terms': 'Terms',
  '/blog': 'Blog',
  '/company': 'Company',
};

function getTitleForPath(pathname: string): string | undefined {
  // Exact match first
  if (routeTitles[pathname]) return routeTitles[pathname];
  // Try the base segment (e.g. /move/some-content → Movement)
  const base = '/' + pathname.split('/').filter(Boolean)[0];
  return routeTitles[base];
}

const contentNoticePaths = ['/', '/home', '/move', '/mental-health', '/nutrition', '/academy', '/motivation'];

export type MainLayoutComponentProps = {};

export const MainLayout: React.FC<MainLayoutComponentProps> = observer(() => {
  const { pathname } = useLocation();
  const hasContentNotice = contentNoticePaths.some((path) => matchPath({ path, end: true }, pathname));
  usePageTitle(getTitleForPath(pathname));

  const { trackingProperties, trackPlausible } = usePlausibleTracking();

  const [showDepartmentModal, setShowDepartmentModal] = useState(false);

  // Auto-trigger department selection like the mobile app:
  // If user has groups available but hasn't selected one yet, force selection
  useEffect(() => {
    Promise.all([CustomerGroupApi.list(), CustomerGroupApi.current()])
      .then(([groups, current]) => {
        if (groups.length > 0 && !current) {
          setShowDepartmentModal(true);
        }
      })
      .catch(() => {
        // Silently ignore - user might not have a gym
      });
  }, []);

  return (
    <React.Fragment>
      <Header />
      <Content>
        {!hasContentNotice && <ServiceLifecycleCard />}
        <PlausibleProvider value={{ trackingProperties, trackPlausible }}>
          <Outlet />
        </PlausibleProvider>
      </Content>
      <Footer />
      <CustomerGroupSelectionModal isOpen={showDepartmentModal} onClose={() => setShowDepartmentModal(false)} />
    </React.Fragment>
  );
});
